if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Clinic selection enforcement
        this.requiresClinic = this.submitButton.dataset.requiresClinic === 'true';
        this.noClinics = this.submitButton.dataset.noClinics === 'true';
        this.clinicHelper = this.querySelector('.product-form__clinic-helper');
        this.noClinicHelper = this.querySelector('.product-form__no-clinic-helper');

        // Only enable variant input if user doesn't require clinic selection
        if (!this.noClinics && !this.requiresClinic) {
          this.variantIdInput.disabled = false;
        }

        if (this.noClinics) {
          // User has no clinics - disable button permanently
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('disabled');
          if (this.noClinicHelper) this.noClinicHelper.classList.remove('hidden');
        } else if (this.requiresClinic) {
          // User has clinics but needs to select one
          this.checkClinicSelection();
          document.addEventListener('clinic:selected', () => {
            this.checkClinicSelection();
          });
        }
      }

      checkClinicSelection() {
        const selectedClinic = sessionStorage.getItem('selected_clinic');
        const isSelected = !!selectedClinic;

        if (isSelected) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('disabled');
          if (this.clinicHelper) this.clinicHelper.classList.add('hidden');
          if (this.variantIdInput) this.variantIdInput.disabled = false;
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('disabled');
          if (this.clinicHelper) this.clinicHelper.classList.remove('hidden');
          if (this.variantIdInput) this.variantIdInput.disabled = true;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.noClinics) {
          // User has no clinics - prevent submission
          if (this.noClinicHelper) this.noClinicHelper.classList.remove('hidden');
          return;
        }

        if (this.requiresClinic) {
          const selectedClinic = sessionStorage.getItem('selected_clinic');
          if (!selectedClinic) {
            if (this.clinicHelper) this.clinicHelper.classList.remove('hidden');
            return;
          }
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          // Only enable if user doesn't need clinic selection or has selected one
          if (this.noClinics) {
            // User has no clinics - keep disabled and show appropriate message
            this.submitButton.setAttribute('disabled', 'disabled');
            if (this.noClinicHelper) this.noClinicHelper.classList.remove('hidden');
            if (this.clinicHelper) this.clinicHelper.classList.add('hidden');
          } else if (this.requiresClinic) {
            // User has clinics - check if one is selected
            const selectedClinic = sessionStorage.getItem('selected_clinic');
            if (selectedClinic) {
              this.submitButton.removeAttribute('disabled');
              if (this.clinicHelper) this.clinicHelper.classList.add('hidden');
            } else {
              this.submitButton.setAttribute('disabled', 'disabled');
              if (this.clinicHelper) this.clinicHelper.classList.remove('hidden');
            }
            if (this.noClinicHelper) this.noClinicHelper.classList.add('hidden');
          } else {
            // Guest or no clinic requirement - enable normally
            this.submitButton.removeAttribute('disabled');
            if (this.clinicHelper) this.clinicHelper.classList.add('hidden');
            if (this.noClinicHelper) this.noClinicHelper.classList.add('hidden');
          }
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
